// ServicesOfProvidersForm.js
import React, { useState, useEffect } from "react";
import { API_IP } from "../../env";
import axios from "axios";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import InputGroup from "react-bootstrap/InputGroup";
import Form from "react-bootstrap/Form";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Collapse from "react-bootstrap/Collapse"; // Import Collapse
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

function ServicesOfProvidersFrom({ show, onHide, providerID, providerName }) {
  // State for the services table
  const [serviceLists, setServiceLists] = useState([]);

  // State for the "Add New" dropdown
  const [availableServices, setAvailableServices] = useState([]);

  // State for toggling the add form
  const [showAddForm, setShowAddForm] = useState(false);

  // State for the new service inputs
  const [newServiceId, setNewServiceId] = useState("");
  const [newServicePrice, setNewServicePrice] = useState("");

  async function axiosReqConfig(url, method, data) {
    const bearerToken = localStorage.getItem("@uphealth_login_token");
    const reqConfig = {
      url: url,
      method: method,
      headers: { Authorization: `Bearer ${bearerToken}` },
      data: data,
    };
    return await axios(reqConfig);
  }

  async function getAllServicesOfTheProvider() {
    if (!providerID) return;
    try {
      // API now returns an object with two lists
      const { data } = await axiosReqConfig(
        `${API_IP}/api/admin/providers/${providerID}`,
        "get",
        []
      );
      setServiceLists(data.providerServices);
      setAvailableServices(data.availableServices);
    } catch (err) {
      console.error("Failed to fetch services", err);
    }
  }

  async function deleteAService(provider_id, care_service_id) {
    if (window.confirm("Are you sure you want to delete this service?")) {
      await axiosReqConfig(`${API_IP}/api/admin/services`, "delete", {
        provider_id: provider_id,
        care_service_id: care_service_id,
      });
      getAllServicesOfTheProvider(); // Refresh list
    }
  }

  async function updateServicePrice(
    provider_id,
    care_service_id,
    currentPrice
  ) {
    let newPrice = prompt("Enter new price (numbers only):", currentPrice);
    if (newPrice != null && !isNaN(newPrice) && newPrice.trim() !== "") {
      await axiosReqConfig(`${API_IP}/api/admin/services`, "patch", {
        provider_id: provider_id,
        care_service_id: care_service_id,
        newPrice: newPrice,
      });
      getAllServicesOfTheProvider(); // Refresh list
    } else if (newPrice !== null) {
      alert("Invalid input. Please enter numbers only.");
    }
  }

  // --- NEW: Function to save the new service ---
  async function handleSaveNewService() {
    // Validation
    if (!newServiceId) {
      alert("Please select a service from the dropdown.");
      return;
    }
    if (
      !newServicePrice ||
      isNaN(newServicePrice) ||
      newServicePrice.trim() === ""
    ) {
      alert("Please enter a valid price.");
      return;
    }

    try {
      // Send care_service_id instead of serviceName
      await axiosReqConfig(`${API_IP}/api/admin/services`, "post", {
        care_service_id: newServiceId,
        provider_id: providerID,
        price: newServicePrice,
      });

      // Reset form and refresh data
      getAllServicesOfTheProvider();
      setNewServiceId("");
      setNewServicePrice("");
      setShowAddForm(false);
    } catch (err) {
      alert("Error adding service: " + (err.response?.data || err.message));
      console.error(err);
    }
  }

  useEffect(() => {
    // Fetch services when modal is shown
    if (show) {
      getAllServicesOfTheProvider();
      // Reset form state when opening
      setShowAddForm(false);
      setNewServiceId("");
      setNewServicePrice("");
    }
  }, [show, providerID]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Care Services Of {providerName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* --- NEW: Add Service Form --- */}
        <Collapse in={showAddForm}>
          <div className="p-3 mb-3 bg-light rounded-3 border">
            <h5>Add a New Service</h5>
            <Row>
              <Col md={7}>
                <Form.Group>
                  <Form.Label>Available Services</Form.Label>
                  <Form.Select
                    value={newServiceId}
                    onChange={(e) => setNewServiceId(e.target.value)}
                  >
                    <option value="">-- Select a service --</option>
                    {availableServices.map((service) => (
                      <option
                        key={service.care_service_id}
                        value={service.care_service_id}
                      >
                        {service.care_service_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <Form.Label>Price ($/hr)</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      placeholder="e.g., 25"
                      value={newServicePrice}
                      onChange={(e) => setNewServicePrice(e.target.value)}
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            <div className="mt-3 text-end">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddForm(false)}
                className="me-2"
              >
                Cancel
              </Button>
              <Button
                variant="success"
                size="sm"
                onClick={handleSaveNewService}
              >
                Save Service
              </Button>
            </div>
          </div>
        </Collapse>
        {/* --- END: Add Service Form --- */}

        <Table striped bordered hover responsive>
          <thead className="table-dark">
            <tr>
              <th>Service Name</th>
              <th>Price</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {serviceLists.map((item, index) => (
              <tr key={index}>
                <td>{item.care_service_name}</td>
                <td>
                  {/* Made the price clickable for editing */}
                  <Button
                    variant="link"
                    className="p-0"
                    onClick={() => {
                      updateServicePrice(
                        providerID,
                        item.care_service_id,
                        item.price
                      );
                    }}
                  >
                    ${item.price}/h
                  </Button>
                </td>
                <td>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => {
                      deleteAService(providerID, item.care_service_id);
                    }}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer className="justify-content-between">
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        {/* This button now toggles the form */}
        <Button variant="success" onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? "Cancel" : "+ Add New Service"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ServicesOfProvidersFrom;
